import { apiNum } from "./math";

/**
 * Account-level margin figures when the backend cannot value the account.
 *
 * While any live position is unpriced the backend sends `null` for equity,
 * initial / maintenance margin, total_margin_used, cross_margin_ratio, ... (unless
 * on-chain cross state supplies them). `Number(null)` is 0, and every utilization
 * built from a 0 read as "0% used" (a healthy, blue/green account) or, for the
 * liquidation banner, "equity <= 0" (a false 100% liquidation alarm). These return
 * `undefined` instead, so callers can show the value as unknown.
 */
export interface AccountMarginFields {
  equity?: string | number | null;
  initial_margin?: string | number | null;
  maintenance_margin?: string | number | null;
  total_margin_used?: string | number | null;
  cross_margin_ratio?: string | number | null;
}

/** Maintenance-margin utilization in percent (100 = liquidation), or undefined. */
export const accountMaintenanceUtilization = (
  account?: AccountMarginFields | null
): number | undefined => {
  if (!account) return undefined;
  // The API's cross_margin_ratio (maintenance_margin_used / equity, a fraction).
  const ratio = apiNum(account.cross_margin_ratio);
  if (ratio !== undefined) return ratio * 100;
  const equity = apiNum(account.equity);
  const maintenanceMargin = apiNum(account.maintenance_margin);
  if (equity === undefined || maintenanceMargin === undefined || equity === 0) {
    return undefined;
  }
  return (maintenanceMargin / equity) * 100;
};

/** Initial-margin utilization in percent, or undefined. */
export const accountInitialUtilization = (
  account?: AccountMarginFields | null
): number | undefined => {
  if (!account) return undefined;
  const equity = apiNum(account.equity);
  if (equity === undefined || equity === 0) return undefined;
  // The API's total_margin_used / equity.
  const totalMarginUsed = apiNum(account.total_margin_used);
  if (totalMarginUsed !== undefined) return (totalMarginUsed / equity) * 100;
  const initialMargin = apiNum(account.initial_margin);
  const maintenanceMargin = apiNum(account.maintenance_margin);
  if (initialMargin === undefined || maintenanceMargin === undefined) {
    return undefined;
  }
  return ((initialMargin + maintenanceMargin) / equity) * 100;
};

/**
 * How close the account is to liquidation, 0..100 (mirrors the backend's
 * liquidationProximity), or `undefined` when it cannot be known.
 */
export const accountLiquidationProximity = (
  account?: AccountMarginFields | null
): number | undefined => {
  if (!account) return 0;
  const maintenanceMargin = apiNum(account.maintenance_margin);
  // No maintenance requirement → no risk.
  if (maintenanceMargin !== undefined && maintenanceMargin <= 0) return 0;
  const equity = apiNum(account.equity);
  // Non-positive equity with a requirement → at/past the liquidation point.
  if (maintenanceMargin !== undefined && equity !== undefined && equity <= 0) {
    return 100;
  }
  const ratio = apiNum(account.cross_margin_ratio);
  if (ratio !== undefined) return Math.min(100, Math.max(0, ratio * 100));
  if (maintenanceMargin === undefined || equity === undefined) return undefined;
  return Math.min(100, (maintenanceMargin / equity) * 100);
};
